import { ArrowLeft, RefreshCcw } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { SingleValue } from "react-select";

import { SelectInput } from "components/Select";
import type { Props as SelectProps } from "react-select";
import { Button } from "components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useGetStoresOptionsQuery } from "@/services/store/select-options";
import { useMeQuery } from "@/services/user";
import { formatOptionLabel } from "@/utils/format-option-label";
import { isUserAdminInGroup } from "@/utils/user";

import type { IDepositStoreHeader } from "src/@types/deposit-reconciliation";
import { storeLabel } from "../utils/store-label";
import PeriodRangeFilter from "@/components/PeriodRangeFilter";
import ReprocessModal from "../../components/reprocess-modal";
import type { OptionType } from "../types";

const StoreSelect = SelectInput as (
  props: SelectProps<OptionType, false>,
) => JSX.Element;

interface StoreHeaderProps {
  storeHeader: IDepositStoreHeader;
  periodStart?: string;
  periodEnd?: string;
  includeReconciled: boolean;
  hasUnsavedSelection: boolean;
}

function Field({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex flex-col">
      <span className="text-xs text-slate-500">{label}</span>
      <span className="text-sm font-medium text-slate-900">{value ?? "—"}</span>
    </div>
  );
}

export default function StoreHeader({
  storeHeader,
  periodStart,
  periodEnd,
  includeReconciled,
  hasUnsavedSelection,
}: StoreHeaderProps) {
  const navigate = useNavigate();
  const { data: storeOptions } = useGetStoresOptionsQuery(undefined, {
    refetchOnMountOrArgChange: false,
  });

  const [pendingNav, setPendingNav] = useState<null | (() => void)>(null);
  const [reprocessOpen, setReprocessOpen] = useState(false);

  const { data: currentUser } = useMeQuery();
  const moduleAccess = localStorage.getItem("@verzel:module");
  const isAdmin = isUserAdminInGroup(currentUser, moduleAccess ?? "");

  const goTo = (storeCode: number, start?: string, end?: string) => {
    const params = new URLSearchParams();
    if (start) params.append("periodStart", start);
    if (end) params.append("periodEnd", end);
    if (includeReconciled) params.append("includeReconciled", "true");
    navigate(`/conciliacao/depositos/${storeCode}?${params.toString()}`);
  };

  const tryNavigate = (storeCode: number, start?: string, end?: string) => {
    const sameStore = storeCode === storeHeader.storeCode;
    const sameStart = (start ?? "") === (periodStart ?? "");
    const sameEnd = (end ?? "") === (periodEnd ?? "");
    if (sameStore && sameStart && sameEnd) return;

    const exec = () => goTo(storeCode, start, end);
    if (hasUnsavedSelection) {
      setPendingNav(() => exec);
    } else {
      exec();
    }
  };

  const handleStoreChange = (option: SingleValue<OptionType>) => {
    if (option?.value) tryNavigate(Number(option.value), periodStart, periodEnd);
  };

  const handlePeriodChange = (
    start: string | undefined,
    end: string | undefined,
  ) => {
    tryNavigate(storeHeader.storeCode, start, end);
  };

  const handleBack = (e: React.MouseEvent) => {
    if (hasUnsavedSelection) {
      e.preventDefault();
      setPendingNav(() => () => navigate("/conciliacao/depositos"));
    }
  };

  const confirmNav = () => {
    pendingNav?.();
    setPendingNav(null);
  };

  const cancelNav = () => setPendingNav(null);

  const currentStoreOption = (storeOptions ?? []).find(
    (o) => Number(o.value) === storeHeader.storeCode,
  ) ?? {
    value: String(storeHeader.storeCode),
    label: `Loja ${storeLabel(storeHeader)}`,
  };

  return (
    <div className="mb-4">
      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-3">
        <div className="flex items-center gap-3 mb-3">
          <Link
            to="/conciliacao/depositos"
            onClick={handleBack}
            className="flex items-center gap-1 text-sm text-[#2B0C1A] hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </Link>
          <div className="h-5 w-px bg-slate-300" />
          <span className="text-xs text-slate-500 uppercase tracking-wide font-medium flex-1">
            Contexto da conciliação
          </span>
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setReprocessOpen(true)}
              className="border-[#2B0C1A] text-[#2B0C1A] hover:bg-[#2B0C1A] hover:text-white"
            >
              <RefreshCcw className="w-3.5 h-3.5 mr-1" />
              Reprocessar esta loja
            </Button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Loja</label>
            <StoreSelect
              options={storeOptions ?? []}
              value={currentStoreOption}
              onChange={handleStoreChange}
              isClearable={false}
              classNamePrefix="an-simple-select"
              formatOptionLabel={formatOptionLabel}
              placeholder="Selecionar loja"
            />
          </div>
          <PeriodRangeFilter
            start={periodStart}
            end={periodEnd}
            onChange={handlePeriodChange}
          />
        </div>
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
        <div className="flex items-center gap-6 flex-wrap">
          <Field
            label="Loja"
            value={storeLabel(storeHeader)}
          />
          <Field label="Gerente" value={storeHeader.manager} />
          <Field label="Regional" value={storeHeader.regional} />
          <Field label="Conferente" value={storeHeader.conferente} />
        </div>
      </div>

      <AlertDialog open={pendingNav !== null}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Descartar seleção?</AlertDialogTitle>
            <AlertDialogDescription>
              Você tem itens selecionados que ainda não foram salvos como
              rascunho. Trocar de loja ou período vai descartar essa seleção.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelNav}>
              Continuar editando
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmNav}
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              Descartar e mudar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ReprocessModal
        open={reprocessOpen}
        onClose={() => setReprocessOpen(false)}
        defaultPeriodStart={periodStart}
        defaultPeriodEnd={periodEnd}
      />
    </div>
  );
}
